import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney, formatApiError, todayISO } from "../lib/api";
import { toast } from "sonner";
import {
  FileText, FileDown, Search, Filter, Flame, Truck, ArrowLeftRight, Package,
  Trash2, RefreshCcw, CheckCircle2, Circle, AlertTriangle, ChevronDown, ChevronUp,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const TYPE_FILTERS = [
  { v: "", label: "All", icon: FileText },
  { v: "sale", label: "Sales", icon: Package },
  { v: "purchase", label: "Purchases", icon: Truck },
  { v: "transfer", label: "Transfers", icon: ArrowLeftRight },
  { v: "refill", label: "Refills", icon: Flame },
  { v: "bulk_purchase", label: "Bulk Buy", icon: Flame },
  { v: "adjustment", label: "Adjust", icon: Filter },
  { v: "defective", label: "Defective", icon: Filter },
];

const TYPE_CLR = {
  sale: "bg-[#DCFCE7] text-[#15803D]",
  purchase: "bg-[#DBEAFE] text-[#1D4ED8]",
  transfer: "bg-[#F3E8FF] text-[#7E22CE]",
  refill: "bg-[#FFEDD5] text-[#9A3412]",
  bulk_purchase: "bg-[#FEF3C7] text-[#B45309]",
  adjustment: "bg-gray-100 text-gray-700",
  defective: "bg-[#FEE2E2] text-[#B91C1C]",
};

export default function Challans() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");
  const [from, setFrom] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(todayISO());
  const [q, setQ] = useState("");
  const [verified, setVerified] = useState(""); // "" all | "false" pending | "true" verified
  const [expanded, setExpanded] = useState({}); // txn id -> bool

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType) params.type = filterType;
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      if (verified) params.verified = verified;
      const { data } = await api.get("/challans", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filterType, from, to, verified]);

  const rows = useMemo(() => {
    const r = data?.rows || [];
    const s = q.trim().toLowerCase();
    return s ? r.filter((x) =>
      (x.challan_no || "").toLowerCase().includes(s) ||
      (x.party_name || "").toLowerCase().includes(s) ||
      (x.vehicle_no || "").toLowerCase().includes(s) ||
      (x.driver_name || "").toLowerCase().includes(s)
    ) : r;
  }, [data, q]);

  const filteredSummary = useMemo(() => ({
    count: rows.length,
    total_kg: rows.reduce((s, r) => s + (r.total_kg || 0), 0),
    total_amount: rows.reduce((s, r) => s + (r.total_amount || 0), 0),
    total_mt: rows.reduce((s, r) => s + (r.mt_qty || 0), 0),
    total_filled: rows.reduce((s, r) => s + (r.filled_qty || 0), 0),
  }), [rows]);

  const exportCsv = () => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    const p = new URLSearchParams();
    if (from) p.set("date_from", from);
    if (to) p.set("date_to", to);
    window.open(`${apiUrl}/api/exports/challans?${p.toString()}`, "_blank");
  };

  const removeTxn = async (row) => {
    if (!isAdmin) return;
    if (!window.confirm(`Delete ${row.type} CH ${row.challan_no || row.id.slice(0, 8)} dated ${row.date}? Stock and ledgers will recompute.`)) return;
    try {
      await api.delete(`/transactions/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleVerify = async (row) => {
    try {
      await api.post(`/transactions/${row.id}/verify-challan`, { verified: !row.challan_verified });
      toast.success(row.challan_verified ? "Marked unverified" : "✓ Physical challan verified");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleDispatch = async (row, kind, currentValue) => {
    try {
      await api.post(`/transactions/${row.id}/dispatch-flag`, { kind, verified: !currentValue });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="challans-page">
      {/* Heading */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <FileText size={20} className="text-black" />
          <h2 className="font-heading font-black text-2xl tracking-tight">Challans</h2>
        </div>
        <span className="text-xs text-gray-500 border border-gray-200 rounded-sm px-2 py-0.5">
          Every entry · sale, purchase, transfer, refill, bulk
        </span>
        <button onClick={load} className="ml-auto h-9 px-3 border border-gray-300 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 hover:border-black" data-testid="challans-refresh">
          <RefreshCcw size={13} /> Refresh
        </button>
        <button onClick={exportCsv} className="h-9 px-3 bg-black text-white rounded-sm text-xs font-semibold inline-flex items-center gap-1.5" data-testid="challans-export">
          <FileDown size={13} /> CSV
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card-flat p-5">
          <div className="small-label text-gray-500">Entries</div>
          <div className="data-num text-3xl mt-1" data-testid="challans-count">{filteredSummary.count}</div>
        </div>
        <div className="card-flat p-5 bg-black text-white border-black">
          <div className="small-label text-white/70 flex items-center gap-1.5"><Flame size={12} /> Total KG</div>
          <div className="data-num text-3xl mt-1" data-testid="challans-kg">{formatMoney(Math.round(filteredSummary.total_kg * 100) / 100)}</div>
        </div>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500">Total ₹</div>
          <div className="data-num text-3xl mt-1" data-testid="challans-amount">₹ {formatMoney(filteredSummary.total_amount)}</div>
        </div>
        <div className="card-flat p-5 bg-[#DCFCE7] border-[#BBF7D0]">
          <div className="small-label text-[#15803D] flex items-center gap-1.5"><CheckCircle2 size={12} /> Physical verified</div>
          <div className="data-num text-3xl mt-1 text-[#15803D]" data-testid="challans-verified">{data?.summary?.verified_count || 0}</div>
        </div>
        <div className="card-flat p-5 bg-[#FEF3C7] border-[#FDE68A]">
          <div className="small-label text-[#B45309] flex items-center gap-1.5"><AlertTriangle size={12} /> Pending verify</div>
          <div className="data-num text-3xl mt-1 text-[#B45309]" data-testid="challans-pending-verify">{data?.summary?.unverified_count || 0}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card-flat p-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {TYPE_FILTERS.map((f) => {
            const Icon = f.icon;
            return (
              <button key={f.v || "all"} onClick={() => setFilterType(f.v)}
                className={`px-3 h-9 rounded-sm text-xs font-semibold border flex items-center gap-1.5 ${filterType === f.v ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-300 hover:border-black"}`}
                data-testid={`type-${f.v || "all"}`}>
                <Icon size={12} /> {f.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 ml-2 text-xs text-gray-600">
          <input type="date" className="input-lg h-9" value={from}
            onChange={(e) => setFrom(e.target.value)} data-testid="challans-from" />
          <span>→</span>
          <input type="date" className="input-lg h-9" value={to}
            onChange={(e) => setTo(e.target.value)} data-testid="challans-to" />
        </div>
        <select className="input-lg h-9 w-40" value={verified} onChange={(e) => setVerified(e.target.value)} data-testid="challans-verified-filter">
          <option value="">All challans</option>
          <option value="false">Not verified</option>
          <option value="true">Verified only</option>
        </select>
        <div className="ml-auto relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="input-lg h-9 w-64 pl-8" placeholder="Challan / party / vehicle / driver…"
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="challans-search" />
        </div>
      </div>

      {/* Table */}
      <div className="card-flat overflow-auto">
        <table className="table-dense w-full">
          <thead>
            <tr>
              <th className="text-left">Date</th>
              <th className="text-left">Challan</th>
              <th className="text-left">Type</th>
              <th className="text-left">Party</th>
              <th className="text-left">Godown</th>
              <th className="text-left">Cylinders (brand · size · F/MT)</th>
              <th className="num">MT</th>
              <th className="num">Filled (refill)</th>
              <th className="num">Total KG</th>
              <th className="num">Total ₹</th>
              <th className="text-left">Vehicle</th>
              <th className="text-left">Physical Verified</th>
              <th className="text-left">Dispatch verified</th>
              <th className="text-right"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="14" className="py-8 text-center text-sm text-gray-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan="14" className="py-8 text-center text-sm text-gray-400">No challans for this filter.</td></tr>
            )}
            {!loading && rows.map((r) => {
              const open = !!expanded[r.id];
              const items = r.items || [];
              const hasItems = items.length > 0;
              return (
              <React.Fragment key={r.id}>
              <tr data-testid={`challan-row-${r.id}`} className={open ? "bg-gray-50" : ""}>
                <td className="data-num text-xs whitespace-nowrap">{r.date}</td>
                <td className="data-num text-xs font-semibold">
                  {hasItems && (
                    <button onClick={() => setExpanded((m) => ({ ...m, [r.id]: !open }))}
                      className="mr-1 text-gray-400 hover:text-black align-middle"
                      title="Detailed items"
                      data-testid={`expand-${r.id}`}>
                      {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                  )}
                  {r.challan_no || <span className="text-gray-400">—</span>}
                </td>
                <td>
                  <span className={`px-2 py-0.5 text-xs rounded-sm font-semibold ${TYPE_CLR[r.type] || "bg-gray-100"}`}>
                    {r.type === "bulk_purchase" ? "Bulk Buy" : r.type}
                  </span>
                </td>
                <td>
                  {r.party_id ? (
                    <Link to={`/parties/${r.party_id}`} className="font-semibold hover:underline">{r.party_name}</Link>
                  ) : <span className="text-gray-400 text-xs">—</span>}
                  {r.party_type && <div className="text-xs text-gray-500 capitalize">{r.party_type}</div>}
                </td>
                <td className="text-xs text-gray-600">
                  {r.from_godown && <div>From: {r.from_godown}</div>}
                  {r.to_godown && r.to_godown !== r.from_godown && <div>To: {r.to_godown}</div>}
                  {r.bullet_godown && <div className="text-[#B45309]">Bullet: {r.bullet_godown}</div>}
                </td>
                <td className="text-xs">
                  {items.length === 0 && <span className="text-gray-400">—</span>}
                  {items.length > 0 && (
                    <div className="flex flex-wrap gap-1 max-w-[280px]">
                      {items.map((it, i) => (
                        <span key={i} className="inline-flex items-center gap-1 bg-gray-100 rounded-sm px-1.5 py-0.5">
                          <span className="font-semibold">{it.brand}</span>
                          <span className="text-gray-500">{it.size}</span>
                          {Number(it.filled_qty) !== 0 && (
                            <span className="text-[#15803D] font-bold">{it.filled_qty > 0 ? "+" : ""}{it.filled_qty}F</span>
                          )}
                          {Number(it.mt_qty) !== 0 && (
                            <span className="text-[#B45309] font-bold">{it.mt_qty > 0 ? "+" : ""}{it.mt_qty}MT</span>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="num text-[#B45309]">{r.mt_qty || ""}</td>
                <td className="num text-[#15803D]">{r.filled_qty || ""}</td>
                <td className="num font-bold">{r.total_kg ? formatMoney(r.total_kg) : ""}</td>
                <td className="num">{r.total_amount ? formatMoney(r.total_amount) : ""}</td>
                <td className="text-xs">
                  {r.vehicle_no || ""}
                  {r.driver_name && <div className="text-gray-500">{r.driver_name}</div>}
                </td>
                <td>
                  <button onClick={() => toggleVerify(r)}
                    className={`text-xs font-semibold flex items-center gap-1.5 px-2 py-1 rounded-sm border ${r.challan_verified ? "bg-[#DCFCE7] text-[#15803D] border-[#15803D]" : "bg-white text-gray-500 border-gray-300 hover:border-black hover:text-black"}`}
                    data-testid={`verify-ch-${r.id}`}>
                    {r.challan_verified
                      ? <><CheckCircle2 size={12} /> Verified</>
                      : <><Circle size={12} /> Mark verified</>
                    }
                  </button>
                  {r.challan_verified && r.challan_verified_by && (
                    <div className="text-[10px] text-gray-400 mt-0.5" data-testid={`verify-ch-by-${r.id}`}>
                      by {r.challan_verified_by}
                      {r.challan_verified_at && <> · {r.challan_verified_at.slice(0, 10)}</>}
                    </div>
                  )}
                </td>
                <td>
                  <div className="grid grid-cols-2 gap-1">
                    {[
                      { k: "challan_whatsapp", label: "Challan · WA", cur: r.challan_sent_whatsapp },
                      { k: "ledger_whatsapp",  label: "Ledger · WA",  cur: r.ledger_sent_whatsapp },
                      { k: "ledger_email",     label: "Ledger · Em",  cur: r.ledger_sent_email },
                      { k: "bill_whatsapp",    label: "Bill · WA",    cur: r.bill_sent_whatsapp },
                      { k: "bill_email",       label: "Bill · Em",    cur: r.bill_sent_email },
                    ].map((f) => (
                      <button
                        key={f.k}
                        onClick={() => toggleDispatch(r, f.k, f.cur)}
                        className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-sm border flex items-center gap-1 whitespace-nowrap ${f.cur ? "bg-[#DCFCE7] text-[#15803D] border-[#15803D]" : "bg-white text-gray-500 border-gray-300 hover:border-black hover:text-black"}`}
                        data-testid={`dispatch-${f.k}-${r.id}`}
                        title={f.cur ? `Sent — click to unmark` : `Click to mark ${f.label} as sent`}
                      >
                        {f.cur ? <CheckCircle2 size={10} /> : <Circle size={10} />}
                        {f.label}
                      </button>
                    ))}
                  </div>
                </td>
                <td className="text-right">
                  {isAdmin && (
                    <button onClick={() => removeTxn(r)} className="text-gray-400 hover:text-[#B91C1C]" title="Delete" data-testid={`challan-del-${r.id}`}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </td>
              </tr>
              {open && hasItems && (
                <tr className="bg-gray-50" data-testid={`items-${r.id}`}>
                  <td></td>
                  <td colSpan="13" className="py-2 pr-4">
                    <div className="small-label text-gray-500 mb-1.5">Item breakdown ({items.length} line{items.length > 1 ? "s" : ""})</div>
                    <div className="overflow-auto">
                      <table className="table-dense w-full max-w-2xl">
                        <thead>
                          <tr>
                            <th className="text-left">Brand</th>
                            <th className="text-left">Size</th>
                            <th className="num">Filled / Refill</th>
                            <th className="num">MT (empty)</th>
                            <th className="num">KG</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((it, i) => {
                            const skg = parseFloat(String(it.size || "").toLowerCase().replace("kg", "").trim()) || 0;
                            const kg = Number(it.filled_qty || 0) * skg;
                            return (
                              <tr key={i}>
                                <td className="font-semibold">{it.brand}</td>
                                <td>{it.size}</td>
                                <td className="num text-[#15803D] font-bold">{it.filled_qty || ""}</td>
                                <td className="num text-[#B45309] font-bold">{it.mt_qty || ""}</td>
                                <td className="num data-num">{formatMoney(Math.round(kg * 100) / 100)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
              );
            })}
          </tbody>
          {!loading && rows.length > 0 && (
            <tfoot>
              <tr className="font-bold bg-gray-50">
                <td colSpan="6" className="text-right pr-3">Totals · {filteredSummary.count} entries</td>
                <td className="num text-[#B45309]">{filteredSummary.total_mt}</td>
                <td className="num text-[#15803D]">{filteredSummary.total_filled}</td>
                <td className="num">{formatMoney(Math.round(filteredSummary.total_kg * 100) / 100)}</td>
                <td className="num">{formatMoney(filteredSummary.total_amount)}</td>
                <td colSpan="3"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
